import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit3, Trash2, Settings, List, Eye, EyeOff, Save, X } from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'
import { simpleAttributesService } from '../../services/api'

const AttributesTab = () => {
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [newAttributeName, setNewAttributeName] = useState('')
  const [expandedAttribute, setExpandedAttribute] = useState(null)
  const [newVariation, setNewVariation] = useState({ title: '' })
  const [editingVariation, setEditingVariation] = useState(null)
  const [isAddingVariation, setIsAddingVariation] = useState(false)
  
  const queryClient = useQueryClient()

  // Fetch attributes
  const { data: attributes = [], isLoading, error } = useQuery({
    queryKey: ['admin-attributes'],
    queryFn: () => simpleAttributesService.getAttributes()
  })

  // Fetch variations for expanded attribute
  const { data: variations = [] } = useQuery({
    queryKey: ['admin-variations', expandedAttribute],
    queryFn: () => expandedAttribute ? simpleAttributesService.getAttributeVariations(expandedAttribute) : Promise.resolve([]),
    enabled: !!expandedAttribute
  })

  // Create attribute mutation
  const createAttributeMutation = useMutation({
    mutationFn: (name) => simpleAttributesService.createAttribute({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-attributes'])
      setNewAttributeName('')
    }
  })

  // Update attribute mutation
  const updateAttributeMutation = useMutation({
    mutationFn: ({ id, name }) => simpleAttributesService.updateAttribute(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-attributes'])
      setEditingId(null)
    }
  })

  // Delete attribute mutation
  const deleteAttributeMutation = useMutation({
    mutationFn: (id) => simpleAttributesService.deleteAttribute(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-attributes'])
      setExpandedAttribute(null)
    }
  })

  // Create variation mutation
  const createVariationMutation = useMutation({
    mutationFn: ({ attributeId, variation }) => 
      simpleAttributesService.createAttributeVariation(attributeId, variation),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-variations', expandedAttribute])
      setNewVariation({ title: '' })
      setIsAddingVariation(false)
    }
  })

  // Update variation mutation
  const updateVariationMutation = useMutation({
    mutationFn: ({ id, variation }) => 
      simpleAttributesService.updateVariation(id, variation),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-variations', expandedAttribute])
      setEditingVariation(null)
    }
  })

  // Delete variation mutation
  const deleteVariationMutation = useMutation({
    mutationFn: (id) => simpleAttributesService.deleteVariation(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-variations', expandedAttribute])
    }
  })

  const handleAddAttribute = () => {
    if (newAttributeName.trim()) {
      createAttributeMutation.mutate(newAttributeName.trim())
    }
  }

  const handleStartEdit = (id, name) => {
    setEditingId(id)
    setEditingName(name)
  }

  const handleSaveEdit = () => {
    if (editingName.trim()) {
      updateAttributeMutation.mutate({ id: editingId, name: editingName.trim() })
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditingName('')
  }

  const handleDeleteAttribute = (id, name) => {
    if (window.confirm(`هل أنت متأكد من حذف الخاصية "${name}"؟ سيتم حذف جميع القيم المرتبطة بها أيضاً.`)) {
      deleteAttributeMutation.mutate(id)
    }
  }

  const handleToggleVariations = (attributeId) => {
    if (expandedAttribute === attributeId) {
      setExpandedAttribute(null)
    } else {
      setExpandedAttribute(attributeId)
      setIsAddingVariation(false)
      setEditingVariation(null)
    }
  }

  const handleAddVariation = () => {
    if (newVariation.title.trim() && expandedAttribute) {
      createVariationMutation.mutate({
        attributeId: expandedAttribute,
        variation: newVariation
      })
    }
  }

  const handleEditVariation = (variation) => {
    setEditingVariation({
      id: variation.id,
      title: variation.title
    })
  }

  const handleSaveVariation = () => {
    if (editingVariation.title.trim()) {
      updateVariationMutation.mutate({
        id: editingVariation.id,
        variation: {
          title: editingVariation.title
        }
      })
    }
  }

  const handleDeleteVariation = (id, title) => {
    if (window.confirm(`هل أنت متأكد من حذف القيمة "${title}"؟`)) {
      deleteVariationMutation.mutate(id)
    }
  }

  if (isLoading) {
    return <LoadingSpinner size="lg" className="py-12" />
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <Settings className="h-16 w-16 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">خطأ في تحميل الخصائص</h3>
        <p className="text-gray-600">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">خصائص المنتجات وقيمها</h2>
        <p className="text-gray-600 mt-1">إدارة خصائص المنتجات مثل اللون والحجم والمادة وقيمها المختلفة</p>
      </div>

      {/* Add New Attribute */}
      <div className="bg-white rounded-lg border mb-6 p-4">
        <div className="flex items-center gap-3">
          <Plus className="h-5 w-5 text-blue-500" />
          <h3 className="font-medium text-gray-900">إضافة خاصية جديدة</h3>
        </div>
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            placeholder="اسم الخاصية (مثل: اللون، الحجم، المادة)"
            value={newAttributeName}
            onChange={(e) => setNewAttributeName(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyPress={(e) => e.key === 'Enter' && handleAddAttribute()}
          />
          <button
            onClick={handleAddAttribute}
            disabled={!newAttributeName.trim() || createAttributeMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createAttributeMutation.isPending ? 'إضافة...' : 'إضافة'}
          </button>
        </div>
      </div>

      {/* Attributes List */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <List className="h-5 w-5" />
            الخصائص المتاحة ({attributes.length})
          </h3>
        </div>

        {attributes.length === 0 ? (
          <div className="text-center py-12">
            <Settings className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد خصائص</h3>
            <p className="text-gray-600">ابدأ بإضافة خاصية جديدة للمنتجات</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {attributes.map((attribute) => (
              <div key={attribute.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {editingId === attribute.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSaveEdit()
                            if (e.key === 'Escape') handleCancelEdit()
                          }}
                          autoFocus
                        />
                        <button
                          onClick={handleSaveEdit}
                          disabled={updateAttributeMutation.isPending}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          حفظ
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                        >
                          إلغاء
                        </button>
                      </div>
                    ) : (
                      <div>
                        <h4 className="font-medium text-gray-900">{attribute.name}</h4>
                        <p className="text-sm text-gray-500 mt-1">
                          تم الإنشاء: {new Date(attribute.created_at).toLocaleDateString('ar-SA')}
                        </p>
                      </div>
                    )}
                  </div>

                  {editingId !== attribute.id && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleVariations(attribute.id)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                        title="إدارة القيم"
                      >
                        {expandedAttribute === attribute.id ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleStartEdit(attribute.id, attribute.name)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="تعديل الاسم"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteAttribute(attribute.id, attribute.name)}
                        disabled={deleteAttributeMutation.isPending}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="حذف الخاصية"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Variations Section */}
                {expandedAttribute === attribute.id && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-gray-700">قيم الخاصية</h5>
                      <button
                        onClick={() => setIsAddingVariation(!isAddingVariation)}
                        className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        {isAddingVariation ? 'إلغاء' : 'إضافة قيمة'}
                      </button>
                    </div>

                    {/* Add New Variation Form */}
                    {isAddingVariation && (
                      <div className="mb-3 p-3 bg-gray-50 rounded border">
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            placeholder="اسم الخاصية (مثل: أحمر، كبير)"
                            value={newVariation.title}
                            onChange={(e) => setNewVariation({ ...newVariation, title: e.target.value })}
                            className="px-2 py-1 border border-gray-300 rounded text-sm flex-1"
                          />
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={handleAddVariation}
                            disabled={!newVariation.title.trim() || createVariationMutation.isPending}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                          >
                            حفظ
                          </button>
                          <button
                            onClick={() => {
                              setIsAddingVariation(false)
                              setNewVariation({ title: '' })
                            }}
                            className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                          >
                            إلغاء
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Variations List */}
                    <div className="space-y-2">
                      {variations.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">لا توجد قيم لهذه الخاصية</p>
                      ) : (
                        variations.map((variation) => (
                          <div key={variation.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                            {editingVariation && editingVariation.id === variation.id ? (
                              <div className="flex-1 flex items-center gap-2">
                                <input
                                  type="text"
                                  value={editingVariation.title}
                                  onChange={(e) => setEditingVariation({ ...editingVariation, title: e.target.value })}
                                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                                <button
                                  onClick={handleSaveVariation}
                                  className="p-1 text-green-600 hover:bg-green-100 rounded"
                                >
                                  <Save className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setEditingVariation(null)}
                                  className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="flex-1">
                                  <span className="text-sm font-medium">{variation.title}</span>
                                </div>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleEditVariation(variation)}
                                    className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                  >
                                    <Edit3 className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteVariation(variation.id, variation.title)}
                                    className="p-1 text-red-600 hover:bg-red-100 rounded"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default AttributesTab
